import { english, turkish, type MessageKey } from "./messages"

export const locales = ["tr", "en"] as const
export type Locale = (typeof locales)[number]
export const localeCookie = "qih-locale"
export function resolveLocale(value: unknown): Locale { return value === "en" ? "en" : "tr" }
export function isLocale(value: unknown): value is Locale { return value === "tr" || value === "en" }
export function translate(locale: Locale, key: MessageKey, values: Record<string, string | number> = {}): string {
  const message = (locale === "en" ? english : turkish)[key]
  return message.replace(/\{([a-zA-Z]+)\}/g, (placeholder, name: string) => Object.hasOwn(values, name) ? String(values[name]) : placeholder)
}
/** Only for locally defined presentation messages, never API errors or user content. */
export function translateMessage(locale: Locale, message: string): string {
  return Object.hasOwn(english, message) ? translate(locale, message as MessageKey) : message
}
/** A caught error is shown only when it carries one of our own messages; browser and runtime text gets the fallback. */
export function knownFailureMessage(failure: unknown, fallback: MessageKey): MessageKey {
  return failure instanceof Error && Object.hasOwn(english, failure.message) ? failure.message as MessageKey : fallback
}
// Unbounded on purpose: locales and option sets come from code, so the caches stay small.
const numberFormats = new Map<string, Intl.NumberFormat>()
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>()
function formatKey(locale: string, options: object | undefined): string {
  const entries = Object.entries(options ?? {}).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
  return `${locale}\u0000${JSON.stringify(entries)}`
}
/** A shared Intl.NumberFormat for this locale and options; construct once, reuse on every render and row. */
export function numberFormat(locale: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = formatKey(locale, options)
  let format = numberFormats.get(key)
  if (!format) numberFormats.set(key, format = new Intl.NumberFormat(locale, options))
  return format
}
/** A shared Intl.DateTimeFormat for this locale and options; construct once, reuse on every render and row. */
export function dateTimeFormat(locale: string, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = formatKey(locale, options)
  let format = dateTimeFormats.get(key)
  if (!format) dateTimeFormats.set(key, format = new Intl.DateTimeFormat(locale, options))
  return format
}
/** A calendar day (YYYY-MM-DD) in the interface language, without time or zone shifts. */
export function formatDay(locale: Locale, value: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00Z`) : new Date(Number.NaN)
  return Number.isNaN(date.valueOf()) ? "—" : dateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    dateStyle: "medium", timeZone: "UTC",
  }).format(date)
}
export function formatDate(locale: Locale, value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.valueOf()) ? "—" : dateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    dateStyle: "medium", timeStyle: "short", timeZone: "UTC",
  }).format(date) + " UTC"
}
